export const DEFAULT_SOURCE_FILE_NAME = 'main.py';

/**
 * Representation of a code file.
 *
 * @typedef {Object} SourceFile
 * @property {string} filename
 * @property {string} code
 */

/**
 * Transform a string into a valid python variable name; snake case, only alphanumerical chars abd `_`
 *
 * @param {string} name - asset name
 * @returns {string}
*/
function makeValidName(name) {
  function isUpperCase(c) { return c.match(/[A-Z]/) !== null; }
  function isLowerCase(c) { return c.match(/[a-z]/) !== null; }
  function isAlphaNumerical(c) { return c.match(/[A-Za-z\d]/) !== null; }

  let last = '';
  let upperCaseSequence = false;
  let out = '';

  for (const c of name) {
    const lastCaseUpper = isUpperCase(last);
    const lastCaseLower = isLowerCase(last);

    const caseUpper = isUpperCase(c);
    const caseLower = isLowerCase(c);

    if (
      caseUpper && lastCaseLower ||
      caseLower && upperCaseSequence
    ) { out += '_';}

    last = c;
    out += isAlphaNumerical(c) ? c : '_';

    upperCaseSequence = lastCaseUpper && caseUpper;
  }

  return out.toLowerCase();
}

function makeSafeString(string) {
  if(/["']/.test(string)) { return `'''${string}'''`; }
  return `"${string}"`;
}

/**
 * Default source file content.
 * @returns {SourceFile}
*/
export function createDefaultSourceFile(library, documentationUrl) {
  function isPneumatic(axis) { return /pneumatic/i.test(axis.getType()); }
  function isBagGripper(axis) { return /pneumatic_bag_gripper/i.test(axis.getType()); }
  function isAC(axis) { return /ac_motor_with_vfd/i.test(axis.getType()); }

  const NEW_LINE = "";
  const configurationList = library.getConfigManager().getConfigurationList();

  const exceptionsImport = new Set(['MachineException']);

  const lines = [
    "from machinelogic import Machine",
    /* we will import exceptions for the assets here, will be inserted here after configs are generated */
    NEW_LINE,
    "### Configuration ###",
    NEW_LINE,
    "# The following code has been automatically generated from the configuration.",
    "# If the configuration changes, please update the code below, and ensure that the names match.",
    NEW_LINE,
    "machine = Machine()",
    NEW_LINE,
  ];

  for (const configuration of configurationList) {
    const controllerName = configuration.getController().getName();
    const validControllerName = makeValidName(controllerName);
    lines.push("try:");
    lines.push(`    my_${validControllerName} = machine.get_machine_motion(${makeSafeString(controllerName)})`);
    lines.push("except MachineException as err:");
    lines.push(`    print(f"Unable to retrieve MachineMotion with name ${controllerName}. Reason: {str(err)}")`);
    lines.push("    raise SystemExit");
    lines.push(NEW_LINE);

    // get assets
    const { actuators, pneumatics, bagGrippers, acActuators } = configuration.listAxes().reduce((accumulator, axis) => {
      if (isAC(axis)) { accumulator.acActuators.push(axis); }
      else if (isBagGripper(axis)) { accumulator.bagGrippers.push(axis); }
      else if( isPneumatic(axis)) { accumulator.pneumatics.push(axis); }
      else { accumulator.actuators.push(axis);  }
      return accumulator;
    }, {actuators:[], pneumatics:[], bagGrippers: [], acActuators:[]});
    const inputs = configuration.listInputs();
    const outputs = configuration.listOutputs();

    const hasAssets = actuators.length || pneumatics.length || bagGrippers.length || acActuators.length || inputs.length || outputs.length;

    if (!hasAssets) {
      continue;
    }

    exceptionsImport.add('MachineMotionException');

    lines.push("try:");

    // actuators
    if (actuators.length) { exceptionsImport.add('ActuatorException'); }
    for (const actuator of actuators) {
      const name = actuator.getName();
      const validName = makeValidName(name);
      lines.push(`    my_${validName} = machine.get_actuator(${makeSafeString(name)})`);
    }

    // inputs
    if (inputs.length) { exceptionsImport.add('InputException'); }
    for (const input of inputs) {
      const name = input.getName();
      const validName = makeValidName(name);
      lines.push(`    my_${validName} = machine.get_input(${makeSafeString(name)})`);
    }

    // outputs
    if (outputs.length) { exceptionsImport.add('OutputException'); }
    for (const output of outputs) {
      const name = output.getName();
      const validName = makeValidName(name);
      lines.push(`    my_${validName} = machine.get_output(${makeSafeString(name)})`);
    }

    lines.push("except MachineMotionException as err:");
    lines.push(`    print(f"Unable to retrieve a device. Reason: {str(err)}")`);
    lines.push(`    raise SystemExit`);

    lines.push(NEW_LINE);
  }

  lines.push(
    "### Program ###",
    NEW_LINE,
    "# Start coding here!",
    `# Documentation can be found at ${documentationUrl}`,
    NEW_LINE,
    'print("Hello, Vention")',
    NEW_LINE,
  );


  lines.splice(1, 0, `from machinelogic import ${[...exceptionsImport].join(', ')}`);

  const code = lines.join('\n');

  return {
    filename: DEFAULT_SOURCE_FILE_NAME,
    code: code
  };
}
